import React, { useMemo } from 'react';
import { Box, DateInput, dateValidatorByFormat, Hint, InfoOutlineIcon, Inscription, ListItem, Position, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { DEFAULT_DATE_FORMAT, REQUIRED_DATE_TEXT } from '@/common/constants';
import { ListItemType } from '@/common/types';

const StyledInfoIcon = styled(InfoOutlineIcon)`
  fill: ${({ theme }) => theme.colors.iconDisabled};
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    opacity: 1 ;
    fill: ${({ theme }) => theme.colors.iconAccent};
  }
`;

const dateValidator = (dateString?: string, showError?: boolean) => {
  if (!dateString && showError) return REQUIRED_DATE_TEXT;
  if (!dateValidatorByFormat(dateString, DEFAULT_DATE_FORMAT) && showError) return 'Invalid date format';
  return undefined;
};

interface Props {
  planId?: string,
  planName?: string,
  endOfCoverage?: string,
  lastDayToSubmitClaim?: string,
  planStartDate?: string,
  planEndDate?: string,
  handleSetEndOfCoverage?: (date: string) => void,
  handleSetLastDayToSubmitClaim?: (date: string) => void,
  isEndOfCoverageDisabled?: boolean,
  isSubmitted?: boolean,
  viewMode?: boolean,
  isClaimLastDateError?: boolean,
  isEndOfCoverageError?: boolean,
}

export const usePlanTerminationCoverageDates = ({
  planId,
  planName,
  endOfCoverage,
  lastDayToSubmitClaim,
  handleSetEndOfCoverage,
  handleSetLastDayToSubmitClaim,
  isEndOfCoverageDisabled,
  isSubmitted,
  viewMode,
  planStartDate,
  planEndDate,
  isClaimLastDateError = false,
  isEndOfCoverageError = false,
}: Props) => {
  const minDate = useMemo(() => {
    if (planStartDate) {
      return dayjs(planStartDate);
    }
    return undefined;
  }, [planStartDate]);
  const formattedPlanFields = useMemo(() => {
    const formatted = [{
      id: planId || '',
      fields: [
        {
          key: 'planName',
          type: ListItemType.Node,
          hasTip: true,
          value: planName || '',
          title: planName || '',
          flex: 2.5,
          node: (
            <Text
              title={planName || ''}
              size="medium"
              weight="bold"
              color="textBody"
              margin={{ right: 'spacing16' }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {planName || ''}
            </Text>
          ),
        },
        {
          key: 'planStartDate',
          type: ListItemType.Text,
          value: planStartDate || '-',
          title: planStartDate || '-',
          fieldStyle: { alignItems: 'flex-start', flex: 1 },
        },
        {
          key: 'planEndDate',
          type: ListItemType.Text,
          value: planEndDate || '-',
          title: planEndDate || '-',
          fieldStyle: { alignItems: 'flex-start', flex: 1 },
        }
      ],
    }];
    return formatted;
  }
  , [planId, planName, planStartDate, planEndDate]);

  const formattedCoverageFields = useMemo(() => {
    const formatted = [{
      id: planId || '',
      fields: [
        {
          key: 'planName',
          type: ListItemType.Node,
          hasTip: true,
          value: planName || '',
          title: planName || '',
          flex: 1.7,
          node: (
            <Text
              title={planName || ''}
              size="medium"
              weight="bold"
              color="textBody"
              margin={{ right: 'spacing16' }}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {planName || ''}
            </Text>
          ),
        },
        {
          key: 'endOfCoverage',
          type: viewMode? ListItemType.Text : ListItemType.Node,
          value: endOfCoverage || '-',
          title: endOfCoverage || '-',
          fieldStyle: { alignItems: 'flex-start', flex: '1.1' },
          node: isEndOfCoverageDisabled ? 
          (
            <Box
              direction="row"
              align="center"
              width={{ max: '150px'}}
              height={{ max: '40px', min: '40px' }}
              gap="spacing8"
            >
              <Inscription
                color="textBody"
                lineHeight="20px"
              >
                {endOfCoverage || '-'}
              </Inscription>
              <Hint
                hintAlign="vertical"
                hintWidth={230}
                hintElement={(
                  <StyledInfoIcon />
                )}
              >
                <Inscription textAlign='center'>
                  This plan pays claims outside coverage period so the coverage will end on the runout when terminated.
                </Inscription>
              </Hint>
            </Box>
          )
          : (
            <Box width={{ max: '150px'}}>
              <DateInput
                id='PlanTerminationCoverageDatesForm_endOfCoverage'
                testId="PlanTerminationCoverageDatesForm_endOfCoverage"
                className="period-picker"
                placeholder='Select date'
                isPicker
                errorText={dateValidator(endOfCoverage, isSubmitted)}
                onChange={handleSetEndOfCoverage}
                value={endOfCoverage || ''}
                pickerSide={Position.BOTTOM}
                isError={isEndOfCoverageError}
                minDate={minDate}
              />  
            </Box>
          ),
        },
        {
          key: 'lastDayToSubmitClaim',
          type: viewMode? ListItemType.Text : ListItemType.Node,
          value: lastDayToSubmitClaim || '-',
          title: lastDayToSubmitClaim || '-',
          fieldStyle: { alignItems: 'flex-start', flex: '1' },
          node: (
            <Box width={{ max: '150px'}}>
              <DateInput
                id='PlanTerminationCoverageDatesForm_lastDayToSubmitClaim'
                testId="PlanTerminationCoverageDatesForm_lastDayToSubmitClaim"
                className="period-picker"
                placeholder='Select date'
                isPicker
                errorText={dateValidator(lastDayToSubmitClaim, isSubmitted)}
                onChange={(value) => {
                  handleSetLastDayToSubmitClaim && handleSetLastDayToSubmitClaim(value);
                  if (isEndOfCoverageDisabled && handleSetEndOfCoverage) {
                    handleSetEndOfCoverage(value);
                  }
                }}
                value={lastDayToSubmitClaim || ''}
                pickerSide={Position.BOTTOM}
                isError={isClaimLastDateError}
                minDate={minDate}
              />  
            </Box>
          ),
        }
      ],
    }];
    return formatted;
  }, [
    planId, 
    planName, 
    endOfCoverage, 
    handleSetEndOfCoverage, 
    lastDayToSubmitClaim, 
    handleSetLastDayToSubmitClaim,
    isSubmitted,
    isEndOfCoverageDisabled,
    viewMode,
    isClaimLastDateError,
    isEndOfCoverageError,
    minDate,
  ]);

  return {
    coverageData: formattedCoverageFields as ListItem[],
    planData: formattedPlanFields as ListItem[],
  };
};
